// Per-avatar character animation clips (idle / running / correct / wrong, the
// two bridges, and the ready and taunt one-shots). Pure data module: no React, no DOM. `ms` is one pass of the clip.
// The animated headshot loops (-head-idle) were never drawn and are no longer
// shipped (TAS-531); their masters are in git at ea4a1b1f.
// `headBox` is where the headshot was cropped from the idle still, as
// [left, top, right, bottom] in AVATAR_FRAME_PX space: the pop-out opens from it.
// Every box follows one standard: top of the head (the skull where the hair
// starts, not an afro, horns, ears or spikes) to chin is 73.4 px of the 130 px
// headshot, the top of the head sits 19 px down it and the face is centred, so
// a box may run past the 256 px frame (transparent padding); hair or horns
// above the line may leave the tile. Ghost, Baby Chick, Crown and Robot keep the
// 19 px head top but not the 73.4 px chin line: at that size they read bigger
// than the rest (Mel, 30 Oct 2026), so Ghost, Chick and Crown show the whole
// character like Octopus and Pink Heart, and Robot's head is 0.64 of the tile
// wide like Ninja's and Skeleton's. Grapes shows the whole character with its
// leaf too, so its bunch top sits at about 29 px, below the line (Mel's choice,
// 30 Oct 2026).

// Every clip and still is a square frame this many pixels across.
export const AVATAR_FRAME_PX = 256;
import { BATCH_BC } from './avatarMotionBatchBC';
import { FINAL_THREE } from './avatarMotionFinalThree';
import { getFollowClipSrc, getMidClipSrc, getSmallClipSrc } from './avatarMotionSmall';
import teddyNormalRunning from '../assets/avatar-motion/teddy/teddy-normal-running.webp';
import teddyNormalRunningStill from '../assets/avatar-motion/teddy/teddy-normal-running.static.webp';

import dinoIdle from '../assets/avatar-motion/dino/dino-idle.webp';
import dinoRunning from '../assets/avatar-motion/dino/dino-running.webp';
import dinoCorrect from '../assets/avatar-motion/dino/dino-correct.webp';
import dinoWrong from '../assets/avatar-motion/dino/dino-wrong.webp';
import dinoIdleToRun from '../assets/avatar-motion/dino/dino-idle-to-run.webp';
import dinoRunToIdle from '../assets/avatar-motion/dino/dino-run-to-idle.webp';
import dinoIdleStill from '../assets/avatar-motion/dino/dino-idle.static.webp';
import dinoRunningStill from '../assets/avatar-motion/dino/dino-running.static.webp';
import dinoCorrectStill from '../assets/avatar-motion/dino/dino-correct.static.webp';
import dinoWrongStill from '../assets/avatar-motion/dino/dino-wrong.static.webp';
import dinoHead from '../assets/avatar-motion/dino/dino-head.webp';
import dinoReady from '../assets/avatar-motion/dino/dino-ready.webp';
import dinoReadyStill from '../assets/avatar-motion/dino/dino-ready.static.webp';
import dinoTaunt from '../assets/avatar-motion/dino/dino-taunt.webp';
import dinoTauntStill from '../assets/avatar-motion/dino/dino-taunt.static.webp';
import dinoCelebrate from '../assets/avatar-motion/dino/dino-celebrate.webp';
import dinoCelebrateStill from '../assets/avatar-motion/dino/dino-celebrate.static.webp';
import ninjaIdle from '../assets/avatar-motion/ninja/ninja-idle.webp';
import ninjaRunning from '../assets/avatar-motion/ninja/ninja-running.webp';
import ninjaCorrect from '../assets/avatar-motion/ninja/ninja-correct.webp';
import ninjaWrong from '../assets/avatar-motion/ninja/ninja-wrong.webp';
import ninjaIdleToRun from '../assets/avatar-motion/ninja/ninja-idle-to-run.webp';
import ninjaRunToIdle from '../assets/avatar-motion/ninja/ninja-run-to-idle.webp';
import ninjaIdleStill from '../assets/avatar-motion/ninja/ninja-idle.static.webp';
import ninjaRunningStill from '../assets/avatar-motion/ninja/ninja-running.static.webp';
import ninjaCorrectStill from '../assets/avatar-motion/ninja/ninja-correct.static.webp';
import ninjaWrongStill from '../assets/avatar-motion/ninja/ninja-wrong.static.webp';
import ninjaHead from '../assets/avatar-motion/ninja/ninja-head.webp';
import ninjaReady from '../assets/avatar-motion/ninja/ninja-ready.webp';
import ninjaReadyStill from '../assets/avatar-motion/ninja/ninja-ready.static.webp';
import ninjaTaunt from '../assets/avatar-motion/ninja/ninja-taunt.webp';
import ninjaTauntStill from '../assets/avatar-motion/ninja/ninja-taunt.static.webp';
import ninjaCelebrate from '../assets/avatar-motion/ninja/ninja-celebrate.webp';
import ninjaCelebrateStill from '../assets/avatar-motion/ninja/ninja-celebrate.static.webp';
import unicornIdle from '../assets/avatar-motion/unicorn/unicorn-idle.webp';
import unicornRunning from '../assets/avatar-motion/unicorn/unicorn-running.webp';
import unicornCorrect from '../assets/avatar-motion/unicorn/unicorn-correct.webp';
import unicornWrong from '../assets/avatar-motion/unicorn/unicorn-wrong.webp';
import unicornIdleToRun from '../assets/avatar-motion/unicorn/unicorn-idle-to-run.webp';
import unicornRunToIdle from '../assets/avatar-motion/unicorn/unicorn-run-to-idle.webp';
import unicornIdleStill from '../assets/avatar-motion/unicorn/unicorn-idle.static.webp';
import unicornRunningStill from '../assets/avatar-motion/unicorn/unicorn-running.static.webp';
import unicornCorrectStill from '../assets/avatar-motion/unicorn/unicorn-correct.static.webp';
import unicornWrongStill from '../assets/avatar-motion/unicorn/unicorn-wrong.static.webp';
import unicornHead from '../assets/avatar-motion/unicorn/unicorn-head.webp';
import unicornReady from '../assets/avatar-motion/unicorn/unicorn-ready.webp';
import unicornReadyStill from '../assets/avatar-motion/unicorn/unicorn-ready.static.webp';
import unicornTaunt from '../assets/avatar-motion/unicorn/unicorn-taunt.webp';
import unicornTauntStill from '../assets/avatar-motion/unicorn/unicorn-taunt.static.webp';
import unicornCelebrate from '../assets/avatar-motion/unicorn/unicorn-celebrate.webp';
import unicornCelebrateStill from '../assets/avatar-motion/unicorn/unicorn-celebrate.static.webp';
import octopusIdle from '../assets/avatar-motion/octopus/octopus-idle.webp';
import octopusIdleStill from '../assets/avatar-motion/octopus/octopus-idle.static.webp';
import octopusRunning from '../assets/avatar-motion/octopus/octopus-running.webp';
import octopusRunningStill from '../assets/avatar-motion/octopus/octopus-running.static.webp';
import octopusIdleToRun from '../assets/avatar-motion/octopus/octopus-idle-to-run.webp';
import octopusReady from '../assets/avatar-motion/octopus/octopus-ready.webp';
import octopusReadyStill from '../assets/avatar-motion/octopus/octopus-ready.static.webp';
import octopusCorrect from '../assets/avatar-motion/octopus/octopus-correct.webp';
import octopusCorrectStill from '../assets/avatar-motion/octopus/octopus-correct.static.webp';
import octopusWrong from '../assets/avatar-motion/octopus/octopus-wrong.webp';
import octopusWrongStill from '../assets/avatar-motion/octopus/octopus-wrong.static.webp';
import octopusTaunt from '../assets/avatar-motion/octopus/octopus-taunt.webp';
import octopusTauntStill from '../assets/avatar-motion/octopus/octopus-taunt.static.webp';
import octopusCelebrate from '../assets/avatar-motion/octopus/octopus-celebrate.webp';
import octopusCelebrateStill from '../assets/avatar-motion/octopus/octopus-celebrate.static.webp';
import octopusHead from '../assets/avatar-motion/octopus/octopus-head.webp';
import dancerWomanIdle from '../assets/avatar-motion/dancer-woman/dancer-woman-idle.webp';
import dancerWomanIdleStill from '../assets/avatar-motion/dancer-woman/dancer-woman-idle.static.webp';
import dancerWomanRunning from '../assets/avatar-motion/dancer-woman/dancer-woman-running.webp';
import dancerWomanRunningStill from '../assets/avatar-motion/dancer-woman/dancer-woman-running.static.webp';
import dancerWomanCorrect from '../assets/avatar-motion/dancer-woman/dancer-woman-correct.webp';
import dancerWomanCorrectStill from '../assets/avatar-motion/dancer-woman/dancer-woman-correct.static.webp';
import dancerWomanWrong from '../assets/avatar-motion/dancer-woman/dancer-woman-wrong.webp';
import dancerWomanWrongStill from '../assets/avatar-motion/dancer-woman/dancer-woman-wrong.static.webp';
import dancerWomanIdleToRun from '../assets/avatar-motion/dancer-woman/dancer-woman-idle-to-run.webp';
import dancerWomanReady from '../assets/avatar-motion/dancer-woman/dancer-woman-ready.webp';
import dancerWomanReadyStill from '../assets/avatar-motion/dancer-woman/dancer-woman-ready.static.webp';
import dancerWomanHead from '../assets/avatar-motion/dancer-woman/dancer-woman-head.webp';
import dancerWomanTaunt from '../assets/avatar-motion/dancer-woman/dancer-woman-taunt.webp';
import dancerWomanTauntStill from '../assets/avatar-motion/dancer-woman/dancer-woman-taunt.static.webp';
import dancerWomanCelebrate from '../assets/avatar-motion/dancer-woman/dancer-woman-celebrate.webp';
import dancerWomanCelebrateStill from '../assets/avatar-motion/dancer-woman/dancer-woman-celebrate.static.webp';
import dancerManIdle from '../assets/avatar-motion/dancer-man/dancer-man-idle.webp';
import dancerManIdleStill from '../assets/avatar-motion/dancer-man/dancer-man-idle.static.webp';
import dancerManRunning from '../assets/avatar-motion/dancer-man/dancer-man-running.webp';
import dancerManRunningStill from '../assets/avatar-motion/dancer-man/dancer-man-running.static.webp';
import dancerManCorrect from '../assets/avatar-motion/dancer-man/dancer-man-correct.webp';
import dancerManCorrectStill from '../assets/avatar-motion/dancer-man/dancer-man-correct.static.webp';
import dancerManWrong from '../assets/avatar-motion/dancer-man/dancer-man-wrong.webp';
import dancerManWrongStill from '../assets/avatar-motion/dancer-man/dancer-man-wrong.static.webp';
import dancerManIdleToRun from '../assets/avatar-motion/dancer-man/dancer-man-idle-to-run.webp';
import dancerManReady from '../assets/avatar-motion/dancer-man/dancer-man-ready.webp';
import dancerManReadyStill from '../assets/avatar-motion/dancer-man/dancer-man-ready.static.webp';
import dancerManHead from '../assets/avatar-motion/dancer-man/dancer-man-head.webp';
import dancerManTaunt from '../assets/avatar-motion/dancer-man/dancer-man-taunt.webp';
import dancerManTauntStill from '../assets/avatar-motion/dancer-man/dancer-man-taunt.static.webp';
import dancerManCelebrate from '../assets/avatar-motion/dancer-man/dancer-man-celebrate.webp';
import dancerManCelebrateStill from '../assets/avatar-motion/dancer-man/dancer-man-celebrate.static.webp';
import dragonIdle from '../assets/avatar-motion/dragon/dragon-idle.webp';
import dragonIdleStill from '../assets/avatar-motion/dragon/dragon-idle.static.webp';
import dragonRunning from '../assets/avatar-motion/dragon/dragon-running.webp';
import dragonRunningStill from '../assets/avatar-motion/dragon/dragon-running.static.webp';
import dragonCorrect from '../assets/avatar-motion/dragon/dragon-correct.webp';
import dragonCorrectStill from '../assets/avatar-motion/dragon/dragon-correct.static.webp';
import dragonWrong from '../assets/avatar-motion/dragon/dragon-wrong.webp';
import dragonWrongStill from '../assets/avatar-motion/dragon/dragon-wrong.static.webp';
import dragonIdleToRun from '../assets/avatar-motion/dragon/dragon-idle-to-run.webp';
import dragonReady from '../assets/avatar-motion/dragon/dragon-ready.webp';
import dragonReadyStill from '../assets/avatar-motion/dragon/dragon-ready.static.webp';
import dragonHead from '../assets/avatar-motion/dragon/dragon-head.webp';
import dragonTaunt from '../assets/avatar-motion/dragon/dragon-taunt.webp';
import dragonTauntStill from '../assets/avatar-motion/dragon/dragon-taunt.static.webp';
import dragonCelebrate from '../assets/avatar-motion/dragon/dragon-celebrate.webp';
import dragonCelebrateStill from '../assets/avatar-motion/dragon/dragon-celebrate.static.webp';

const NAMES = ['idle', 'running', 'correct', 'wrong', 'idleToRun', 'runToIdle', 'ready', 'taunt', 'celebrate'];

// runToIdle, taunt and celebrate are optional: an avatar without the asset simply has no
// such clip (getAvatarClip returns null for it).
function build(a, ms) {
  const clip = (key, still, i, loop) => ({ src: a[key], still, ms: ms[i], loop,
    ...(a[`${key}Placement`] && { placement: a[`${key}Placement`] }) });
  const optional = (key, still, i) => (a[key] ? { [key]: clip(key, still, i, false) } : {});
  return {
    head: a.head,
    headBox: a.headBox,
    runOffHoldMs: a.runOffHoldMs ?? 0,
    idle: clip('idle', a.idleStill, 0, true),
    running: clip('running', a.runningStill, 1, true),
    correct: clip('correct', a.correctStill, 2, false),
    wrong: clip('wrong', a.wrongStill, 3, false),
    // Bridges borrow the still of the pose they land on.
    idleToRun: clip('idleToRun', a.runningStill, 4, false),
    ...optional('runToIdle', a.idleStill, 5),
    // One-shots for the ready press and the taunt power.
    ready: clip('ready', a.readyStill, 6, false),
    ...optional('taunt', a.tauntStill, 7),
    // Winner's podium one-shot; avatars without one fall back to correct-then-idle.
    ...optional('celebrate', a.celebrateStill, 8),
  };
}

const REGISTRY = {
  ...Object.fromEntries([...BATCH_BC, ...FINAL_THREE].map(({ emoji, assets, ms }) => [emoji, build(assets, ms)])),
  '🦖': build(
    { idle: dinoIdle, running: dinoRunning, correct: dinoCorrect, wrong: dinoWrong, idleToRun: dinoIdleToRun, runToIdle: dinoRunToIdle, ready: dinoReady, taunt: dinoTaunt, celebrate: dinoCelebrate,
      readyStill: dinoReadyStill, tauntStill: dinoTauntStill, celebrateStill: dinoCelebrateStill,
      idleStill: dinoIdleStill, runningStill: dinoRunningStill, correctStill: dinoCorrectStill, wrongStill: dinoWrongStill, head: dinoHead,
      headBox: [94, 34, 239, 179] },
    [2940, 792, 924, 4364, 833, 2394, 1302, 1470, 2478],
  ),
  '🥷': build(
    { idle: ninjaIdle, running: ninjaRunning, correct: ninjaCorrect, wrong: ninjaWrong, idleToRun: ninjaIdleToRun, runToIdle: ninjaRunToIdle, ready: ninjaReady, taunt: ninjaTaunt, celebrate: ninjaCelebrate,
      readyStill: ninjaReadyStill, tauntStill: ninjaTauntStill, celebrateStill: ninjaCelebrateStill,
      idleStill: ninjaIdleStill, runningStill: ninjaRunningStill, correctStill: ninjaCorrectStill, wrongStill: ninjaWrongStill, head: ninjaHead,
      headBox: [80, 17, 243, 180] },
    [3822, 1375, 1932, 2852, 1275, 1386, 1218, 1134, 2772],
  ),
  '🦄': build(
    { idle: unicornIdle, running: unicornRunning, correct: unicornCorrect, wrong: unicornWrong, idleToRun: unicornIdleToRun, runToIdle: unicornRunToIdle, ready: unicornReady, taunt: unicornTaunt, celebrate: unicornCelebrate,
      readyStill: unicornReadyStill, tauntStill: unicornTauntStill, celebrateStill: unicornCelebrateStill,
      idleStill: unicornIdleStill, runningStill: unicornRunningStill, correctStill: unicornCorrectStill, wrongStill: unicornWrongStill, head: unicornHead,
      headBox: [93, 42, 242, 191] },
    [6000, 1000, 2138, 2264, 1333, 1554, 1260, 1974, 1890],
  ),
  '🐙': build(
    { idle: octopusIdle, running: octopusRunning, correct: octopusCorrect, wrong: octopusWrong, idleToRun: octopusIdleToRun, ready: octopusReady, taunt: octopusTaunt, celebrate: octopusCelebrate,
      readyStill: octopusReadyStill, tauntStill: octopusTauntStill, celebrateStill: octopusCelebrateStill,
      idleStill: octopusIdleStill, runningStill: octopusRunningStill, correctStill: octopusCorrectStill, wrongStill: octopusWrongStill, head: octopusHead,
      headBox: [44, 18, 269, 243] },
    [6000, 667, 1344, 2016, 1775, 0, 1134, 2772, 1722],
  ),
  '💃': build(
    { idle: dancerWomanIdle, running: dancerWomanRunning, correct: dancerWomanCorrect, wrong: dancerWomanWrong, idleToRun: dancerWomanIdleToRun, ready: dancerWomanReady,
      taunt: dancerWomanTaunt, celebrate: dancerWomanCelebrate,
      readyStill: dancerWomanReadyStill, tauntStill: dancerWomanTauntStill, celebrateStill: dancerWomanCelebrateStill,
      idleStill: dancerWomanIdleStill, runningStill: dancerWomanRunningStill, correctStill: dancerWomanCorrectStill, wrongStill: dancerWomanWrongStill, head: dancerWomanHead,
      headBox: [76, 28, 212, 164] },
    [6000, 1333, 4042, 4042, 1442, 0, 4042, 2184, 4042],
  ),
  '🕺': build(
    { idle: dancerManIdle, running: dancerManRunning, correct: dancerManCorrect, wrong: dancerManWrong, idleToRun: dancerManIdleToRun, ready: dancerManReady,
      taunt: dancerManTaunt, celebrate: dancerManCelebrate,
      readyStill: dancerManReadyStill, tauntStill: dancerManTauntStill, celebrateStill: dancerManCelebrateStill,
      idleStill: dancerManIdleStill, runningStill: dancerManRunningStill, correctStill: dancerManCorrectStill, wrongStill: dancerManWrongStill, head: dancerManHead,
      headBox: [85, 52, 209, 176] },
    [6000, 1333, 3150, 3570, 1042, 0, 3066, 2016, 1386],
  ),
  '🐲': build(
    { idle: dragonIdle, running: dragonRunning, correct: dragonCorrect, wrong: dragonWrong, idleToRun: dragonIdleToRun, ready: dragonReady,
      taunt: dragonTaunt, celebrate: dragonCelebrate,
      readyStill: dragonReadyStill, tauntStill: dragonTauntStill, celebrateStill: dragonCelebrateStill,
      idleStill: dragonIdleStill, runningStill: dragonRunningStill, correctStill: dragonCorrectStill, wrongStill: dragonWrongStill, head: dragonHead,
      headBox: [91, 65, 236, 210] },
    [6000, 1333, 1596, 1428, 1233, 0, 1260, 2142, 1512],
  ),
};

export function hasAvatarMotion(emoji) {
  return Object.prototype.hasOwnProperty.call(REGISTRY, emoji);
}

// Teddy plays its complete trip and recovery before sliding out of view.
export function getAvatarRunOffHoldMs(emoji) {
  return hasAvatarMotion(emoji) ? REGISTRY[emoji].runOffHoldMs : 0;
}

// The trip belongs to the lobby. Selection uses the pre-trip stride without a hold.
// `mid` is for a lobby row, which draws the run at about 70 px: the bridge and
// the run come back as their 128 px copies.
export function getAvatarRunOffProfile(emoji, context = 'selection', { mid = false } = {}) {
  return {
    bridge: getAvatarClip(emoji, 'idleToRun', { mid }),
    running: emoji === '🧸' && context !== 'lobby'
      ? { src: teddyNormalRunning, still: teddyNormalRunningStill, ms: 504, loop: true }
      : getAvatarClip(emoji, 'running', { mid }),
    holdMs: context === 'lobby' ? getAvatarRunOffHoldMs(emoji) : 0,
  };
}

// The idle clip with its small lobby copy as `src` (same ms, loop and still); built once per avatar.
const smallIdles = new Map();
function smallIdle(emoji, full) {
  if (!smallIdles.has(emoji)) {
    const src = getSmallClipSrc(emoji, 'idle');
    smallIdles.set(emoji, src ? { ...full, src } : full);
  }
  return smallIdles.get(emoji);
}

// The clip with its mid or follow copy as `src` (same ms, loop and still); built
// once per avatar and clip. A clip without that copy comes back as itself.
const copyClips = new Map();
function copyClip(kind, emoji, name, full) {
  const key = `${kind}:${emoji}:${name}`;
  if (!copyClips.has(key)) {
    // Clip names are camelCase, files kebab-case (idleToRun -> idle-to-run).
    const file = name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
    const src = kind === 'follow' ? getFollowClipSrc(emoji, file) : getMidClipSrc(emoji, file);
    copyClips.set(key, src ? { ...full, src } : full);
  }
  return copyClips.get(key);
}

// -> { src, still, ms, loop } or null when the avatar (or clip name) is unknown.
// `small` is for the 28 px lobby chips: the idle loop comes back as its small
// copy, or as the full clip when the avatar has none. Other clips ignore it.
// `mid` is for places drawn 49 to 96 px (the cast panel's tiles, the lobby
// run-off, the phone's taunt toast): the clip comes back as its 128 px copy
// (correct, celebrate, the run-off pair and taunt have one), or as the full
// clip when there is none.
// `follow` is for the loop a pop-out plays after its one-shot: the idle comes
// back as its 12 fps copy at full size, or as the full clip. Other clips ignore it.
export function getAvatarClip(emoji, name, { small = false, mid = false, follow = false } = {}) {
  if (!hasAvatarMotion(emoji) || !NAMES.includes(name)) return null;
  const clip = REGISTRY[emoji][name] ?? null;
  if (!clip) return null;
  if (small && name === 'idle') return smallIdle(emoji, clip);
  if (follow && name === 'idle') return copyClip('follow', emoji, name, clip);
  return mid ? copyClip('mid', emoji, name, clip) : clip;
}

export function getAvatarStill(emoji) {
  return hasAvatarMotion(emoji) ? REGISTRY[emoji].idle.still : null;
}

// 3/4 headshot crop used for the static avatar picture; null when there is none.
export function getAvatarHead(emoji) {
  return hasAvatarMotion(emoji) ? REGISTRY[emoji].head ?? null : null;
}

// Headshot crop box in the full-body frame ([left, top, right, bottom]); null when there is none.
export function getAvatarHeadBox(emoji) {
  return hasAvatarMotion(emoji) ? REGISTRY[emoji].headBox ?? null : null;
}

// Inside an avatar circle the headshot is framed tighter: drawn this much larger
// about a point a little above its centre, the circle clipping the rest (Mel,
// 2026-10-27, picked from a sheet of zooms: 1.25x about 50% / 40%).
export const CIRCLE_HEAD_ZOOM = 1.25;
export const CIRCLE_HEAD_ORIGIN = [0.5, 0.4];
export const CIRCLE_HEAD_STYLE = {
  transform: `scale(${CIRCLE_HEAD_ZOOM})`,
  transformOrigin: `${CIRCLE_HEAD_ORIGIN[0] * 100}% ${CIRCLE_HEAD_ORIGIN[1] * 100}%`,
};

// The part of a headBox an avatar circle shows, in the same frame space. A
// pop-out from a circle opens from this box, so its first frame is the head
// exactly as the circle drew it.
export function circleHeadBox(headBox) {
  if (!headBox) return headBox;
  const [l, t, r, b] = headBox;
  const [ox, oy] = CIRCLE_HEAD_ORIGIN;
  const w = (r - l) / CIRCLE_HEAD_ZOOM;
  const h = (b - t) / CIRCLE_HEAD_ZOOM;
  const x = l + (r - l - w) * ox;
  const y = t + (b - t - h) * oy;
  return [x, y, x + w, y + h];
}
